﻿/**
 * Every number and date on screen goes through here, so signs, precision and the "no value" dash
 * are the same everywhere. Formatting only: no figure is computed in the client (§22.1).
 */

export const DASH = '—';

const isNum = (v: number | null | undefined): v is number =>
  typeof v === 'number' && Number.isFinite(v);

/**
 * `v` to `digits` significant figures, trailing zeros dropped: `14`, `2.5`, `0.000123`. The dash for
 * anything that is not a finite number — the one safe spelling of `+v.toPrecision(n)`, which throws
 * on `undefined` ("Cannot read properties of undefined (reading 'toPrecision')").
 */
export function sig(v: number | null | undefined, digits = 4): string {
  return isNum(v) ? String(+v.toPrecision(digits)) : DASH;
}

/**
 * An indicator reading that is not a price — the phone's `Fmt.indicatorValue`, verbatim. Whole
 * values plain, two decimals from 0.1 up ("0.50" is the established shape of an ordinary
 * reading), and three significant figures below that: MACD on EUR/USD lives around 0.0001 and a
 * forex daily impulse around 0.006, which two decimals printed as "0.00" and "0.01".
 */
export function indicatorValue(v: number | null | undefined): string {
  if (!isNum(v)) return DASH;
  if (v === 0) return '0'; // covers -0, which Intl prints with its sign
  if (Number.isInteger(v)) return new Intl.NumberFormat('en-GB').format(v);
  const text = Math.abs(v) >= 0.1 ? new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) : sig(v, 3);
  return text === '-0.00' || text === '-0' ? text.slice(1) : text;
}

/** Signed percentage: `+12.3%`, `-4.0%`, `0.0%`. */
export function pct(v: number | null | undefined, dp = 1, signed = true): string {
  if (!isNum(v)) return DASH;
  const s = v.toFixed(dp);
  const zero = Number(s) === 0;
  return `${signed && v > 0 && !zero ? '+' : ''}${zero ? (0).toFixed(dp) : s}%`;
}

/** Unsigned percentage for rates (win rate, hit rate): `62%`. */
export function rate(v: number | null | undefined, dp = 0): string {
  return pct(v, dp, false);
}

/** A 0–1 fraction shown as a percentage. */
export function fraction(v: number | null | undefined, dp = 0): string {
  return isNum(v) ? rate(v * 100, dp) : DASH;
}

export function num(v: number | null | undefined, dp = 2): string {
  if (!isNum(v)) return DASH;
  return v.toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export function int(v: number | null | undefined): string {
  if (!isNum(v)) return DASH;
  return Math.round(v).toLocaleString('en-GB');
}

/** R multiples: `+1.20R`. */
export function rMult(v: number | null | undefined, dp = 2): string {
  if (!isNum(v)) return DASH;
  return `${v > 0 ? '+' : ''}${v.toFixed(dp)}R`;
}

/**
 * A price with precision that suits its magnitude — sub-cent crypto keeps its significant digits
 * (todo "Sub-cent price display").
 */
export function price(v: number | null | undefined): string {
  if (!isNum(v)) return DASH;
  const a = Math.abs(v);
  const dp = a >= 1000 ? 2 : a >= 10 ? 3 : a >= 1 ? 4 : a >= 0.01 ? 5 : 8;
  return v.toLocaleString('en-GB', { minimumFractionDigits: Math.min(dp, 2), maximumFractionDigits: dp });
}

export type Sign = 'pos' | 'neg' | 'zero' | 'none';

export function signOf(v: number | null | undefined): Sign {
  if (!isNum(v)) return 'none';
  return v > 0 ? 'pos' : v < 0 ? 'neg' : 'zero';
}

// ---- dates ----------------------------------------------------------------------------------

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const MONTHS = MON;
export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const toDate = (v: string | number | Date) => (v instanceof Date ? v : new Date(v));

/** `8 Oct 2026` in the viewer's timezone. */
export function date(v: string | number | Date | null | undefined): string {
  if (v == null) return DASH;
  const d = toDate(v);
  if (Number.isNaN(d.getTime())) return DASH;
  return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()}`;
}

/** `8 Oct 26 14:00` — compact, for tables. */
export function dateTime(v: string | number | Date | null | undefined): string {
  if (v == null) return DASH;
  const d = toDate(v);
  if (Number.isNaN(d.getTime())) return DASH;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getDate()} ${MON[d.getMonth()]} ${String(d.getFullYear()).slice(2)} ${hh}:${mm}`;
}

export function time(v: string | number | Date | null | undefined): string {
  if (v == null) return DASH;
  const d = toDate(v);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** `Oct 25` for axis ticks. */
export function monthYear(v: string | number | Date): string {
  const d = toDate(v);
  return `${MON[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
}

/** "3 min ago", "2 h ago", "4 Oct". */
export function relative(v: string | number | Date | null | undefined, now = Date.now()): string {
  if (v == null) return DASH;
  const d = toDate(v);
  const s = Math.round((now - d.getTime()) / 1000);
  if (s < 0) return dateTime(d);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} d ago`;
  return date(d);
}

/** A duration in seconds: `45 min`, `6 h`, `3.5 d`. */
export function duration(seconds: number | null | undefined): string {
  if (!isNum(seconds)) return DASH;
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))} min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(seconds < 36000 ? 1 : 0)} h`;
  return `${(seconds / 86400).toFixed(1)} d`;
}

/** A window length in days: `90 days`, `2 years`, `3.4 years`. */
export function windowLength(days: number | null | undefined): string {
  if (!isNum(days)) return DASH;
  if (days < 365) return `${Math.round(days)} days`;
  const years = days / 365;
  const rounded = Math.round(years * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)} year${rounded === 1 ? '' : 's'}`;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${int(n)} ${n === 1 ? one : many}`;
}

/** `4h`, `1d` timeframes as the API names them, for display. */
export function timeframe(tf: string | null | undefined): string {
  return tf ?? DASH;
}
